import { useCallback, useEffect, useState } from "react";
import { getAllUsers, updateUserType, deleteUser } from "../../../api/controller/userProfile";
import { RegionsCardData } from "../../../api/controller/videoController/regions";

export default function useAdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regions, setRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [regionsError, setRegionsError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchUsers = async () => {
      try {
        const fetched = await getAllUsers();
        if (mounted) setUsers(fetched || []);
      } catch (err) {
        if (mounted) setError(err.message || "Failed to fetch users.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchUsers();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchRegions = async () => {
      setRegionsLoading(true);
      setRegionsError(null);
      try {
        const raw = await RegionsCardData();
        if (mounted) setRegions(Array.isArray(raw) ? raw : []);
      } catch (err) {
        if (mounted) {
          console.error("useAdminUsers: failed to load regions", err);
          setRegions([]);
          setRegionsError(err?.message || "Failed to load regions.");
        }
      } finally {
        if (mounted) setRegionsLoading(false);
      }
    };

    fetchRegions();

    return () => {
      mounted = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const fetched = await getAllUsers();
      setUsers(fetched || []);
    } catch (err) {
      setError(err.message || "Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  }, []);

  const removeUser = useCallback(async (_id) => {
    await deleteUser(_id);
    setUsers((prev) => prev.filter((u) => (u._id || u.id) !== _id));
  }, []);

  const setAdmin = useCallback(async (_id) => {
    await updateUserType(_id, "admin");
    setUsers((prev) => prev.map((u) => ((u._id || u.id) === _id ? { ...u, userType: "admin" } : u)));
  }, []);

  const removeAdmin = useCallback(async (_id) => {
    await updateUserType(_id, "regularuser");
    setUsers((prev) => prev.map((u) => ((u._id || u.id) === _id ? { ...u, userType: "regularuser" } : u)));
  }, []);

  const setUserType = useCallback(async (_id, type) => {
    await updateUserType(_id, type);
    setUsers((prev) => prev.map((u) => ((u._id || u.id) === _id ? { ...u, userType: type } : u)));
  }, []);

  const findRegionById = useCallback(
    (regionId) => {
      if (!regionId) return null;
      const target = regionId.toString().trim();
      if (!target) return null;

      return (
        regions.find((region) => {
          if (!region || region._id == null) return false;
          return region._id.toString().trim() === target;
        }) || null
      );
    },
    [regions]
  );

  return {
    users,
    loading,
    error,
    refresh,
    removeUser,
    setAdmin,
    removeAdmin,
    setUserType,
    regions,
    regionsLoading,
    regionsError,
    findRegionById,
  };
}
